import Backbone from 'backbone';
import { Base64 } from 'js-base64';

import Module from './module';

import { delay } from '../utils/promisify';
import { requestify, promisify } from '../utils/promisify';
import { Router } from './router';

export { 
    routing,
    restore, root, replace, popup,
    closePopup, rejectPopup,
    handle,
};

function routing(application, appState, routes) {
    const router = Router.from( routes );

    let lastModule = null;

    // function to router navigation
    const navigate = (url, options, popupResult) => {
        router.popupResult = popupResult;
        router.navigate( url, options );
    };

    const navigationFn = (moduleUrl, moduleState) => {
        // replace current application's top stack state with new one
        appState.replaceTop( moduleUrl, moduleState );
        navigate( appState.urlify(), { trigger: false } );
    };

    const proxyEventsFn = (eventName, ...args) => 
        router.trigger( eventName, ...args );
    
    router.getChannel()
        .map( ([callback, args]) => callback.apply( null, args ) )
        .filter( routeParams => routeParams && routeParams.openFn )
        .forEach( ({openFn, transformStateFn, transformArgsFn, mod, module, args}) => {
            // console.log( 'ch foreach, args are: ', args );
            // stop listening for navigation events for
            // previously opened module 
            if (lastModule) {
                lastModule.off( 'url:navigate', navigationFn );
                lastModule.off( 'all', proxyEventsFn );
            }

            const options = {
                application,
                appState,
                navigate,
                mod,
                module,
                transformStateFn,
                transformArgsFn,
            };

            // non-module-related-opening aka back
            // (module-related-opening goes below)
            if (!module)
                return requestify( openFn( options, ...args ) );
            
            // listen for events in new module
            module.on( 'url:navigate', navigationFn );
            module.on( 'all', proxyEventsFn );
        
            lastModule = module;
            
            // open module in application by specified function
            return requestify( openFn( options, ...args ) );
        } );

    Backbone.history.start();
}

function restore(options, ...moduleArgs) {
    // console.log( 'called restore, moduleArgs are: ', moduleArgs );

    const {application, appState, mod, module, transformStateFn} = options;
    const [stringifiedAppState, ...otherModuleArgs] = moduleArgs.slice().reverse();
    otherModuleArgs.reverse();

    if (stringifiedAppState)
        ; // console.log( Base64.decode( stringifiedAppState ) );

    if (stringifiedAppState)
        appState.restore( stringifiedAppState );
    else    
        appState.clear();

    const [moduleUrl, moduleState] = appState.getTopState();

    return promisify( transformStateFn( moduleState, ...otherModuleArgs ) )
        .then( ({moduleState, moduleArgs}) => {
            return module.options.isRoot = appState.size() < 2,
                application.open( mod, module, moduleState, ...moduleArgs );
        } );
}

function root(options, ...moduleArgs) {
    const {application, appState, mod, module, transformArgsFn} = options;

    return promisify( transformArgsFn( ...moduleArgs ) )
        .then( ({moduleState, moduleArgs}) => {
            appState.replace( null, moduleState );

            return module.options.isRoot = true,
                application.open( mod, module, moduleState, ...moduleArgs );
        } );
}

function replace(options, ...moduleArgs) {
    const {application, appState, mod, module, transformArgsFn} = options;

    return promisify( transformArgsFn( ...moduleArgs ) )
        .then( ({moduleState, moduleArgs}) => {
            appState.replaceTop( null, moduleState );

            return module.options.isRoot = appState.size() < 2,
                application.open( mod, module, moduleState, ...moduleArgs );
        } );
}

function popup(options, ...moduleArgs) {
    const {application, appState, mod, module, transformArgsFn} = options;

    return promisify( transformArgsFn( ...moduleArgs ) )
        .then( ({moduleState, moduleArgs}) => {
            appState.push( null, moduleState );
            
            return module.options.isRoot = false,
                application.popup( mod, module, moduleState, ...moduleArgs );
        } );
}

function closePopup(options, popupResult) {
    return pop( options, Promise.resolve( popupResult ) );
}

function rejectPopup(options) {
    return pop( options, Promise.reject() );
}

function pop(options, popupResult) {
    const {appState, navigate} = options;

    appState.pop();
    navigate( appState.urlify(), { trigger: true }, popupResult );
}

function handle(openFn, mod, Module, transformFn) {
    const transformStateFn = (moduleState, ...moduleArgs) =>
        makeTransformation( (moduleState, ...moduleArgs) => {
            // console.log( 'callback for t-state:', JSON.stringify( moduleState ), JSON.stringify( ...moduleArgs ) );
            return transformFn( moduleState, ...moduleArgs );
        },
        moduleState,
        ...moduleArgs );

    const transformArgsFn = (...moduleArgs) =>
         makeTransformation( (moduleState, ...moduleArgs) => {
             // console.log( 'callback for t-args:', JSON.stringify( moduleArgs ) );
             return transformFn( ...moduleArgs );
        },
        {},
        ...moduleArgs );
    
    const makeTransformation = (callTransformFn, moduleState, ...moduleArgs) => {
        // console.log( 'make transformation call with state, args:', JSON.stringify( moduleState ), JSON.stringify( moduleArgs ) );

        if (!transformFn)
            return {
                moduleState, 
                moduleArgs,
            };

        return promisify( callTransformFn( moduleState, ...moduleArgs ) ).then( response => {
            if (response instanceof Array)
                return {
                    moduleState,
                    moduleArgs: response,
                };

            if (response.moduleState || response.moduleArgs)
                return {
                    moduleState: response.moduleState || moduleState,
                    moduleArgs: response.moduleArgs || moduleArgs,
                };

            return {
                moduleState,
                moduleArgs: [response],
            };
        } );
    };

    return (...args) => {
        // for "back" actions there is no Module
        const module = Module ? new Module( {} ) : undefined;
        return ({ openFn, transformStateFn, transformArgsFn, module, mod, args });
    };
}



// WEBPACK FOOTER //
// ./src/js/core/routing.js